/**
 * §58 PR 2 — pure helpers for the Plan & Features tab (Paelode-internal wording).
 * OPP PR 6 — shared with the operator company's Plan card (operator templates and features).
 */
import {
  FEATURE_CATALOG,
  OPERATOR_FEATURE_CATALOG,
  type FeatureGroup,
  type FeatureTemplate,
  type HaulerFeatureView,
  type HaulerPlanHistoryEntry,
  type OperatorTemplate,
} from '@lodex-pro/shared/features';

export const TEMPLATE_LABELS: Record<FeatureTemplate, string> = {
  EXPRESS: 'Express',
  STARTER: 'Starter',
  PROFESSIONAL: 'Professional',
  ENTERPRISE: 'Enterprise',
};

/** OPP PR 6 — an operator company's templates. */
export const OPERATOR_TEMPLATE_LABELS: Record<OperatorTemplate, string> = {
  FREE: 'Free',
  BASIC: 'Basic',
  PREMIUM: 'Premium',
  ENTERPRISE: 'Enterprise',
};

export const GROUP_LABELS: Record<FeatureGroup, string> = {
  CORE: 'Always captured',
  COMPLIANCE: 'Compliance reports',
  INTELLIGENCE: 'Intelligence',
  ADVANCED: 'Advanced',
  IOT: 'IoT add-ons',
};

const FEATURE_NAME = new Map<string, string>(
  [...FEATURE_CATALOG, ...OPERATOR_FEATURE_CATALOG].map((f) => [f.id, f.name]),
);

export function featureName(id: unknown): string {
  return typeof id === 'string' ? (FEATURE_NAME.get(id) ?? id) : 'a feature';
}

/** "On", "Off" or "On · 15" for a limited feature. */
export function stateLabel(enabled: boolean, limit: number | null | undefined): string {
  if (!enabled) return 'Off';
  return limit === null || limit === undefined ? 'On' : `On · ${String(limit)}`;
}

// ── The hauler's company-local day (DEC-004; hauler_tenant_settings.timezone) ─────────

/** 'YYYY-MM-DD' of an instant in a timezone. */
export function dateInZone(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** The zone in words for the dialog, e.g. "Central Time"; the IANA id if unnamed. */
export function zoneLabel(tz: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? tz;
  } catch {
    return tz;
  }
}

/** "Nov 30, 2026": an instant as the hauler's local date. */
export function fmtDate(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Milliseconds the zone's wall clock is ahead of UTC at an instant. */
function zoneOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(new Date(utcMs));
  const v = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
  const wall = Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second'));
  return wall - Math.floor(utcMs / 1000) * 1000;
}

/** Where a feature's current state comes from, in words. */
export function sourceLabel(
  f: Pick<HaulerFeatureView, 'effective' | 'override'>,
  tz: string,
): string {
  if (f.effective.source === 'DATA_CAPTURE') return 'Always on';
  if (f.effective.source === 'TEMPLATE') {
    // An override past its expiry no longer counts, but is still stored until cleanup.
    return f.override?.expired === true ? 'Template (trial ended)' : 'Template';
  }
  return f.effective.expiresAt === null
    ? 'Override'
    : `Trial until ${fmtDate(f.effective.expiresAt, tz)}`;
}

export interface TemplateChange {
  readonly id: string;
  readonly name: string;
  readonly from: string;
  readonly to: string;
}

/**
 * What applying `to` changes, feature by feature, against the template the hauler is on.
 * Data capture never changes. A feature with an override is listed apart: the override
 * still wins unless the admin clears it.
 */
export function templateDiff(
  features: readonly HaulerFeatureView[],
  to: FeatureTemplate,
): { changes: TemplateChange[]; overridden: string[] } {
  const target = new Map(FEATURE_CATALOG.map((f) => [f.id as string, f.defaults[to]]));
  const changes: TemplateChange[] = [];
  const overridden: string[] = [];
  for (const f of features) {
    if (f.isDataCapture) continue;
    if (f.override !== null && !f.override.expired) overridden.push(f.name);
    const next = target.get(f.id);
    if (next === undefined) continue;
    const from = stateLabel(f.templateDefault.enabled, f.templateDefault.limit);
    const toLabel = stateLabel(next.enabled, next.limit);
    if (from !== toLabel) changes.push({ id: f.id, name: f.name, from, to: toLabel });
  }
  return { changes, overridden };
}

/** One history row in words. */
export function historySummary(h: HaulerPlanHistoryEntry, tz: string): string {
  const o = h.oldValues ?? {};
  const n = h.newValues ?? {};
  const tpl = (v: unknown): string =>
    typeof v === 'string' && v in TEMPLATE_LABELS
      ? TEMPLATE_LABELS[v as FeatureTemplate]
      : typeof v === 'string' && v in OPERATOR_TEMPLATE_LABELS
        ? OPERATOR_TEMPLATE_LABELS[v as OperatorTemplate]
        : 'no plan (Starter)';
  switch (h.action) {
    case 'TEMPLATE_APPLIED': {
      const cleared = Array.isArray(n.clearedOverrides) ? n.clearedOverrides.length : 0;
      const tail = cleared > 0 ? `, cleared ${String(cleared)} override(s)` : '';
      return `Template ${tpl(o.template)} → ${tpl(n.template)}${tail}`;
    }
    case 'FEATURE_OVERRIDDEN': {
      const to = stateLabel(
        n.enabled === true,
        typeof n.limitValue === 'number' ? n.limitValue : null,
      );
      const until = typeof n.expiresAt === 'string' ? ` until ${fmtDate(n.expiresAt, tz)}` : '';
      return `${featureName(n.featureId)} set ${to}${until}`;
    }
    case 'FEATURE_OVERRIDE_CLEARED':
      return `${featureName(o.featureId ?? n.featureId)} reset to template`;
    case 'TRIAL_EXPIRED':
      return `${featureName(o.featureId ?? n.featureId)} trial ended`;
    case 'EXPRESS_CUSTOMER_SET': {
      // The payload carries ids, not names (§58 PR 4, 0195).
      if (typeof n.mergedFromOperatorId === 'string') {
        return 'Express customer follows an operator merge';
      }
      const head =
        o.template === 'EXPRESS'
          ? 'Express customer changed'
          : `Template ${tpl(o.template)} → Express, with its one customer`;
      const tail =
        n.customerCreated === true
          ? ', customer added'
          : n.customerReactivated === true
            ? ', customer re-activated'
            : '';
      return head + tail;
    }
    case 'UPDATED': {
      // OPP PR 6 — an operator company's user limit (0212).
      if (n.change === 'SEAT_LIMIT_SET') {
        const seats = (v: unknown): string => (typeof v === 'number' ? String(v) : 'no limit');
        return `User limit ${seats(o.userSeatLimit)} → ${seats(n.userSeatLimit)}`;
      }
      return h.action;
    }
    default:
      return h.action;
  }
}

/**
 * A trial's end from a date input ("2026-11-30"): the END of that day in the HAULER's
 * timezone, so a trial "until 30 Nov" includes all of 30 Nov where the hauler works,
 * wherever the admin sits. DST-safe: the offset is taken at the instant itself. Null for an
 * empty input.
 */
export function trialEndIso(dateInput: string, tz: string): string | null {
  if (dateInput === '') return null;
  const [y, m, d] = dateInput.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return null;
  const wall = Date.UTC(y, m - 1, d, 23, 59, 59);
  const first = wall - zoneOffsetMs(wall, tz);
  return new Date(wall - zoneOffsetMs(first, tz)).toISOString();
}
